import * as SwitchPrimitive from 'radix-ui/switch';
import { useId } from 'react';
import { cx } from '../cx';
import { MOTION } from '../motion';

/**
 * A switch is for settings that apply immediately. Anything that needs a Save
 * button is a Checkbox.
 */
export interface SwitchProps {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly label?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly id?: string | undefined;
  readonly className?: string | undefined;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  ariaLabel,
  disabled = false,
  id,
  className,
}: SwitchProps) {
  const fallbackId = useId();
  const controlId = id ?? fallbackId;

  return (
    <div
      className={cx(
        'inline-flex min-h-(--touch-target-min) items-center gap-3 text-sm text-ink select-none',
        disabled && 'opacity-50',
        className,
      )}
    >
      <SwitchPrimitive.Root
        id={controlId}
        checked={checked}
        disabled={disabled}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        onCheckedChange={onCheckedChange}
        className={cx(
          // `hit-expand` closes the OPEN row in ios26-mechanisms.md: the Radix root is
          // drawn 40x24, which is under criterion 1's 40px floor on the short axis, and
          // a fat switch is not the fix because the DRAWING is right. The pseudo-element
          // grows only what the finger can reach (index.css). `touch-manipulation`
          // rather than `pressable`: a switch answers a press by MOVING its thumb, and a
          // surface fill under a track would be a second, contradictory answer.
          'hit-expand touch-manipulation h-6 w-10 shrink-0 rounded-pill border',
          MOTION.state,
          'border-line-strong bg-surface-sunken',
          'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        )}
      >
        {/* White in both themes and both states - Apple's switch changes only
            the track. bg-ink here made the OFF state read as filled in light
            mode (ios26-mechanisms.md, Switch row). */}
        <SwitchPrimitive.Thumb
          className={cx(
            'block size-4.5 translate-x-0.5 rounded-pill bg-switch-thumb shadow-switch-thumb',
            MOTION.move,
            'data-[state=checked]:translate-x-5',
          )}
        />
      </SwitchPrimitive.Root>
      {label !== undefined && <label htmlFor={controlId}>{label}</label>}
    </div>
  );
}
